import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Button, 
  Avatar, 
  Menu, 
  MenuItem, 
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
  Divider,
  Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import GrainIcon from '@mui/icons-material/Grain';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';

// 날씨 정보 컴포넌트
const WeatherInfo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [weatherData, setWeatherData] = useState({
    temperature: 21,
    condition: 'sunny', // sunny, cloudy, rainy, snowy, stormy
    location: '서울특별시',
    humidity: 65,
    windSpeed: 3.5,
    airQuality: '좋음'
  });
  
  // 날씨 아이콘 선택
  const getWeatherIcon = () => {
    switch(weatherData.condition) {
      case 'sunny':
        return <WbSunnyIcon fontSize="small" />;
      case 'cloudy':
        return <CloudIcon fontSize="small" />;
      case 'rainy':
        return <GrainIcon fontSize="small" />;
      case 'snowy':
        return <AcUnitIcon fontSize="small" />;
      case 'stormy':
        return <ThunderstormIcon fontSize="small" />;
      default:
        return <WbSunnyIcon fontSize="small" />;
    }
  };
  
  // 날씨 상태 텍스트
  const getWeatherText = () => {
    switch(weatherData.condition) {
      case 'sunny': return '맑음';
      case 'cloudy': return '흐림';
      case 'rainy': return '비';
      case 'snowy': return '눈';
      case 'stormy': return '폭풍';
      default: return '맑음';
    }
  };
  
  // 모바일에서는 간소화된 날씨 정보 표시
  if (isMobile) {
    return (
      <Chip
        icon={getWeatherIcon()}
        label={`${weatherData.temperature}°C`}
        size="small"
        sx={{
          bgcolor: 'rgba(0, 229, 255, 0.1)',
          color: 'primary.light',
          border: 'none',
          '& .MuiChip-icon': {
            color: 'primary.light',
          },
          height: 28,
        }}
      />
    );
  }
  
  // 데스크톱에서는 상세 날씨 정보 표시
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      bgcolor: 'rgba(0, 229, 255, 0.05)',
      px: 1.5,
      py: 0.5,
      mr: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ 
          mr: 1, 
          color: 'primary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {getWeatherIcon()}
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" fontWeight="medium" color="primary.light">
              {weatherData.temperature}°C
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              {getWeatherText()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary', mr: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {weatherData.location}
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: 'rgba(76, 185, 231, 0.2)' }} />
      
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          습도: {weatherData.humidity}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          미세먼지: <span style={{ color: theme.palette.success.main }}>{weatherData.airQuality}</span>
        </Typography>
      </Box>
    </Box>
  );
};

// 사용자 정보 컴포넌트
const UserInfo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [userData, setUserData] = useState({
    name: '관리자',
    role: '시스템 관리자',
    lastLogin: '2025-05-30 09:15',
    isActive: true
  });
  
  // 모바일에서는 간소화된 사용자 정보 표시
  if (isMobile) {
    return null; // 모바일에서는 공간 제약으로 표시하지 않음
  }
  
  // 데스크톱에서는 사용자 정보 표시
  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      bgcolor: 'rgba(0, 229, 255, 0.05)',
      px: 1.5,
      py: 0.5,
      mr: 2
    }}>
      <Box sx={{ 
        mr: 1, 
        color: 'primary.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <PersonIcon fontSize="small" />
      </Box>
      <Box>
        <Typography variant="body2" fontWeight="medium" color="text.primary">
          <Box 
            component="span" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 255, 255, 0.7)',
              background: 'linear-gradient(90deg, #00E5FF 0%, #4CB9E7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {userData.name}
          </Box>
          님께서 접속중입니다
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {userData.role} | 최근 로그인: {userData.lastLogin}
        </Typography>
      </Box>
    </Box>
  );
};

const Navbar = ({ onToggleSidebar, onLogout, sidebarOpen, sidebarCollapsed, onToggleCollapse }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationMenu = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    if (onLogout) onLogout();
  };

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: 'linear-gradient(90deg, #051336 0%, #0F3460 100%)',
        boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)',
        borderBottom: '1px solid rgba(76, 185, 231, 0.3)',
        borderRadius: 0,
        borderLeft : 0,
        borderRight : 0
      }}
    >
      <Toolbar>
        {isMobile ? (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onToggleSidebar}
            sx={{ 
              mr: 2,
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(0, 229, 255, 0.1)',
                boxShadow: '0 0 8px rgba(0, 229, 255, 0.3)',
              }
            }}
          >
            {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        ) : (
          <IconButton
            color="inherit"
            aria-label="toggle collapse"
            edge="start"
            onClick={onToggleCollapse}
            sx={{ 
              mr: 2,
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(0, 229, 255, 0.1)',
                boxShadow: '0 0 8px rgba(0, 229, 255, 0.3)',
              }
            }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
          </IconButton>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          '& img': {
            height: 70,
            mr: 1,
            mt: 1.5,
            filter: 'drop-shadow(0 0 5px rgba(0, 229, 255, 0.5))'
          }
        }}>
          <img src="/images/logo.svg" alt="Logo" />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              display: { xs: 'none', sm: 'block' },
              fontWeight: 'bold',
              background: 'linear-gradient(90deg, #00E5FF 0%, #4CB9E7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
            }}
          >
            탄소 발자국 관리 시스템
          </Typography>
        </Box>
        
        <Box sx={{ flexGrow: 1 }} />
        
        {/* 날씨 정보 컴포넌트 */}
        <WeatherInfo />

        {/* 사용자 정보 컴포넌트 */}
        <UserInfo />
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="알림">
            <IconButton 
              color="inherit" 
              onClick={handleNotificationMenu}
              sx={{ 
                mx: 1,
                color: 'primary.light',
                '&:hover': {
                  backgroundColor: 'rgba(0, 229, 255, 0.1)',
                  boxShadow: '0 0 8px rgba(0, 229, 255, 0.3)',
                }
              }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          <Menu
            id="notification-menu"
            anchorEl={notificationAnchorEl}
            keepMounted
            open={Boolean(notificationAnchorEl)}
            onClose={handleNotificationClose}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(10, 36, 99, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)',
                border: '1px solid rgba(76, 185, 231, 0.3)',
                mt: 1.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  borderBottom: '1px solid rgba(76, 185, 231, 0.2)',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 229, 255, 0.1)',
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                새로운 데이터가 추가되었습니다
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2">월간 보고서가 준비되었습니다</Typography>
            </MenuItem>
            <MenuItem onClick={handleNotificationClose}>
              <Typography variant="body2">시스템 업데이트 안내</Typography>
            </MenuItem>
          </Menu>
          
          {/* <Tooltip title="설정">
            <IconButton 
              color="inherit" 
              sx={{ 
                mx: 1,
                color: 'primary.light',
                '&:hover': {
                  backgroundColor: 'rgba(0, 229, 255, 0.1)',
                  boxShadow: '0 0 8px rgba(0, 229, 255, 0.3)',
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip> */}
          
          {/* <Tooltip title="도움말">
            <IconButton 
              color="inherit" 
              sx={{ 
                mx: 1,
                color: 'primary.light',
                '&:hover': {
                  backgroundColor: 'rgba(0, 229, 255, 0.1)',
                  boxShadow: '0 0 8px rgba(0, 229, 255, 0.3)',
                }
              }}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip> */}
          
          <Tooltip title="계정">
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{ 
                ml: 1,
                background: 'linear-gradient(45deg, #1E5F8C 0%, #4CB9E7 100%)',
                '&:hover': {
                  boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
                }
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
          </Tooltip>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(10, 36, 99, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)',
                border: '1px solid rgba(76, 185, 231, 0.3)',
                mt: 1.5,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 229, 255, 0.1)',
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleClose}>
              <AccountCircleIcon sx={{ mr: 1, fontSize: 20 }} />
              프로필
            </MenuItem>
            <MenuItem onClick={handleClose}>
              <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
              계정 설정
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              로그아웃
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
